import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Breadcrumb, Button, DatePicker, Input, PageHeader, SelectMenu, SettingsPanel, SettingsRow, Spinner, Switch } from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { openSlotSidebar } from './openSlotSidebar';
import { useConfig } from '../../config/ConfigProvider';
import { apiDelete, apiGet, apiPost, apiPut } from '../../lib/api';

interface BlackoutDto { id: string; title: string; startsAt: string; endsAt: string; startsLabel: string; endsLabel: string; timezone: string }
interface HolidayRow { key: string; name: string; nextLabel: string }
interface CountrySetting { country: string; enabled: boolean; disabled: string[] }
interface HolidaysDto {
  countries: { code: string; name: string }[];
  settings: CountrySetting[];
  holidays: Record<string, HolidayRow[]>;
}

export function TimeOffPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const cfg = useConfig();
  const [blackouts, setBlackouts] = useState<BlackoutDto[] | null>(null);
  const [hol, setHol] = useState<HolidaysDto | null>(null);
  const [country, setCountry] = useState('');
  const [form, setForm] = useState({ title: '', startDate: '', startTime: '', endDate: '', endTime: '' });
  const [err, setErr] = useState<string | null>(null);
  const formComplete = Boolean(form.title && form.startDate && form.startTime && form.endDate && form.endTime);

  const load = useCallback(() => {
    apiGet<BlackoutDto[]>('/api/openslot/blackouts').then(setBlackouts).catch(() => setErr('Failed to load.'));
    apiGet<HolidaysDto>('/api/openslot/holidays').then(setHol).catch(() => setErr('Failed to load.'));
  }, []);
  useEffect(load, [load]);

  // Default the selected country to the first enabled one (else the first available).
  useEffect(() => {
    if (!hol || country) return;
    const enabled = hol.settings.find((s) => s.enabled)?.country;
    setCountry(enabled ?? hol.countries[0]?.code ?? '');
  }, [hol, country]);

  const addBlackout = async () => {
    setErr(null);
    try {
      // Compose the datetime-local payload the API expects from the date + time parts.
      await apiPost('/api/openslot/blackouts', {
        title: form.title,
        startsAt: `${form.startDate}T${form.startTime}`,
        endsAt: `${form.endDate}T${form.endTime}`,
      });
      setForm({ title: '', startDate: '', startTime: '', endDate: '', endTime: '' });
      load();
    } catch {
      setErr('Could not add — check the dates (end must be after start).');
    }
  };

  const settingFor = (code: string): CountrySetting =>
    hol?.settings.find((s) => s.country === code) ?? { country: code, enabled: false, disabled: [] };

  const saveCountry = async (next: CountrySetting) => {
    setHol((h) =>
      h ? { ...h, settings: [...h.settings.filter((s) => s.country !== next.country), next] } : h,
    );
    await apiPut('/api/openslot/holidays', next);
  };

  return (
    <AppChrome secondarySidebar={openSlotSidebar(navigate, pathname)}>
      <PageHeader
        title={<>Open Slot - <span className="fw-normal">Time off</span></>}
        breadcrumb={<Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Open Slot' }, { label: 'Time off' }]} />}
      />
      <div className="content">
        {err && <Alert variant="danger" icon="ph-warning" dismissible>{err}</Alert>}

        <SettingsPanel title="Blackout dates" description="One-off windows when you're unavailable (vacation, etc.).">
          <SettingsRow title="Add a blackout" description={`Times are in the default timezone (${cfg.timezone}).`}>
            <div className="row g-2 align-items-end">
              <div className="col-sm-3"><Input label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div className="col-sm-2"><DatePicker label="Start date" value={form.startDate} onChange={(v) => setForm({ ...form, startDate: v })} /></div>
              <div className="col-sm-2"><Input label="Start time" type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} /></div>
              <div className="col-sm-2"><DatePicker label="End date" value={form.endDate} onChange={(v) => setForm({ ...form, endDate: v })} /></div>
              <div className="col-sm-2"><Input label="End time" type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} /></div>
              <div className="col-sm-1 mb-3"><Button variant="primary" disabled={!formComplete} onClick={addBlackout}>Add</Button></div>
            </div>
          </SettingsRow>
          <SettingsRow title="Scheduled" description={`Existing blackout windows (shown in ${cfg.timezone}).`}>
            {!blackouts && <Spinner size="sm" />}
            {blackouts && blackouts.length === 0 && <span className="text-muted">None.</span>}
            {blackouts?.map((b) => (
              <div key={b.id} className="d-flex align-items-center py-1">
                <span className="fw-semibold me-2">{b.title}</span>
                <span className="text-muted fs-sm">{b.startsLabel} – {b.endsLabel}</span>
                <Button variant="light" size="sm" className="text-danger ms-auto" icon="ph-trash" onClick={() => apiDelete(`/api/openslot/blackouts/${b.id}`).then(load)}>Remove</Button>
              </div>
            ))}
          </SettingsRow>
        </SettingsPanel>

        {!hol ? (
          <div className="card mb-3"><div className="card-body"><Spinner /></div></div>
        ) : (
          (() => {
            const s = settingFor(country);
            const rows = hol.holidays[country] ?? [];
            return (
              <div className="card mb-3">
                {/* Header: country selector + master blocking toggle */}
                <div className="card-header bg-light border-bottom-0 d-flex align-items-center flex-wrap gap-3">
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-semibold text-nowrap">Country for Holidays</span>
                    <div style={{ minWidth: 220 }}>
                      <SelectMenu
                        searchable
                        className="mb-0"
                        value={country}
                        onChange={setCountry}
                        options={hol.countries.map((c) => ({ value: c.code, label: c.name }))}
                      />
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2 ms-auto">
                    <span className="fw-semibold">Blocking holidays</span>
                    <Switch
                      label={<span className="visually-hidden">Blocking holidays</span>}
                      checked={s.enabled}
                      onChange={(e) => saveCountry({ ...s, enabled: e.target.checked })}
                    />
                  </div>
                </div>

                {/* Holidays table */}
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead>
                      <tr className="text-muted small text-uppercase">
                        <th className="ps-3">Holiday</th>
                        <th className="text-end">Next</th>
                        <th className="text-end pe-3">Blocked</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((h) => {
                        const blocked = !s.disabled.includes(h.key);
                        return (
                          <tr key={h.key}>
                            <td className="ps-3 fw-semibold">{h.name}</td>
                            <td className="text-end text-muted">Next: {h.nextLabel}</td>
                            <td className="text-end pe-3">
                              <Switch
                                className="d-inline-block"
                                label={<span className="visually-hidden">Block {h.name}</span>}
                                checked={blocked}
                                disabled={!s.enabled}
                                onChange={(e) =>
                                  saveCountry({
                                    ...s,
                                    disabled: e.target.checked
                                      ? s.disabled.filter((k) => k !== h.key)
                                      : [...s.disabled, h.key],
                                  })
                                }
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="card-footer bg-transparent text-muted fs-sm">
                  Enabled holidays block the entire day across every event type, just like a blackout. The next
                  occurrence rolls forward automatically each year.
                </div>
              </div>
            );
          })()
        )}
      </div>
    </AppChrome>
  );
}
